<script>
  import { page, router } from "@inertiajs/svelte";
  import { propertiesStore } from "../stores/properties.js";
  import {
    Sheet,
    SheetContent,
    SheetTrigger,
  } from "$lib/components/ui/sheet/index.js";
  import * as DropdownMenu from "$lib/components/ui/dropdown-menu/index.js";
  import * as Popover from "$lib/components/ui/popover/index.js";
  import { Input } from "$lib/components/ui/input/index.js";
  import PanelLeftIcon from "@lucide/svelte/icons/panel-left";
  import HomeIcon from "@lucide/svelte/icons/house";
  import ChevronUpDownIcon from "@lucide/svelte/icons/chevrons-up-down";
  import PlusIcon from "@lucide/svelte/icons/plus";
  import ChevronDownIcon from "@lucide/svelte/icons/chevron-down";
  import XIcon from "@lucide/svelte/icons/x";
  import SparklesIcon from "@lucide/svelte/icons/sparkles";
  import FileTextIcon from "@lucide/svelte/icons/file-text";
  import Building2Icon from "@lucide/svelte/icons/building-2";
  import ReceiptIcon from "@lucide/svelte/icons/receipt";
  import ZapIcon from "@lucide/svelte/icons/zap";
  import Table2Icon from "@lucide/svelte/icons/table-2";
  import FileSpreadsheetIcon from "@lucide/svelte/icons/file-spreadsheet";
  import EllipsisVerticalIcon from "@lucide/svelte/icons/ellipsis-vertical";
  import LogOutIcon from "@lucide/svelte/icons/log-out";

  let { children } = $props();

  $effect(() => {
    propertiesStore.set($page.props.properties ?? []);
  });

  let switcherOpen = $state(false);
  let sheetOpen = $state(false);
  let namePopoverOpen = $state(false);
  let nameInput = $state("");

  const currentPropertyId = $derived(
    ($page.url.match(/\/properties\/(\d+)/) ?? [])[1] ?? null,
  );

  const currentPropertyLabel = $derived(
    $propertiesStore.length === 0
      ? "No properties"
      : $propertiesStore.length === 1
        ? $propertiesStore[0].name
        : currentPropertyId
          ? ($propertiesStore.find((p) => String(p.id) === currentPropertyId)
              ?.name ?? "All properties")
          : "All properties",
  );

  const userName = $derived($page.props.current_user_name ?? "");

  $effect(() => {
    if (namePopoverOpen) {
      nameInput = userName;
    }
  });

  const navItems = $derived([
    {
      component: SparklesIcon,
      label: "SmartScan",
      pathMatch: null,
      path: "/properties",
    },
    {
      component: FileTextIcon,
      label: "Income Entry",
      pathMatch: "/rooms",
      path: currentPropertyId ? `/properties/${currentPropertyId}/rooms` : null,
    },
    {
      component: Building2Icon,
      label: "Property Finance",
      pathMatch: "/property_finance",
      path: currentPropertyId
        ? `/properties/${currentPropertyId}/property_finance`
        : null,
    },
    {
      component: ReceiptIcon,
      label: "Statutory Taxes",
      pathMatch: "/statutory_taxes",
      path: currentPropertyId
        ? `/properties/${currentPropertyId}/statutory_taxes`
        : null,
    },
    {
      component: ZapIcon,
      label: "Utilities & Custom",
      pathMatch: "/utilities",
      path: currentPropertyId
        ? `/properties/${currentPropertyId}/utilities`
        : null,
    },
    {
      component: Table2Icon,
      label: "Summary",
      pathMatch: "/summary",
      path: currentPropertyId
        ? `/properties/${currentPropertyId}/summary`
        : null,
    },
  ]);

  function toggleSwitcher(e) {
    e.stopPropagation();
    switcherOpen = !switcherOpen;
  }

  function closeSwitcher() {
    switcherOpen = false;
  }

  function navigateTo(path) {
    switcherOpen = false;
    sheetOpen = false;
    router.visit(path);
  }

  function emailInitials(email) {
    if (!email) return "?";
    return email.charAt(0).toUpperCase();
  }

  async function saveName() {
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");
    const response = await fetch("/profile", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken ?? "",
      },
      body: JSON.stringify({ profile: { name: nameInput } }),
    });
    if (response.ok) {
      namePopoverOpen = false;
      router.reload({ only: ["current_user_name"] });
    }
  }

  function logout() {
    router.delete("/logout");
  }
</script>

<svelte:window onclick={closeSwitcher} />

<div class="flex h-dvh overflow-hidden bg-[#fafafa]">
  <!-- ── Desktop Sidebar ── -->
  <aside
    class="hidden md:flex w-64 flex-shrink-0 flex-col bg-white border-r border-zinc-200 overflow-hidden"
    style="view-transition-name: app-sidebar"
  >
    {@render sidebarContent()}
  </aside>

  <!-- ── Mobile: header + Right Sheet ── -->
  <Sheet bind:open={sheetOpen}>
    <header
      class="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-zinc-200 flex-shrink-0 fixed top-0 left-0 right-0 z-30"
      style="view-transition-name: app-mobile-header"
    >
      <div class="flex items-center gap-2">
        <div
          class="w-8 h-8 bg-zinc-900 rounded-lg flex items-center justify-center text-white flex-shrink-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="3" y1="22" x2="21" y2="22" /><line
              x1="6"
              y1="18"
              x2="6"
              y2="11"
            />
            <line x1="10" y1="18" x2="10" y2="11" /><line
              x1="14"
              y1="18"
              x2="14"
              y2="11"
            />
            <line x1="18" y1="18" x2="18" y2="11" /><polygon
              points="12 2 20 7 4 7"
            />
          </svg>
        </div>
        <span class="text-sm font-semibold text-zinc-900">MyRentalTax</span>
      </div>
      <SheetTrigger>
        {#snippet child({ props })}
          <button
            class="p-1.5 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
            {...props}
            aria-label="Open navigation"
          >
            <PanelLeftIcon class="w-5 h-5" />
          </button>
        {/snippet}
      </SheetTrigger>
    </header>

    <SheetContent
      side="right"
      class="p-0 w-72 flex flex-col"
      showCloseButton={false}
    >
      <!-- Header: logo + close -->
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-zinc-100 flex-shrink-0"
      >
        <div class="flex items-center gap-2">
          <div
            class="w-7 h-7 bg-zinc-900 rounded-md flex items-center justify-center text-white flex-shrink-0"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="3" y1="22" x2="21" y2="22" /><line
                x1="6"
                y1="18"
                x2="6"
                y2="11"
              />
              <line x1="10" y1="18" x2="10" y2="11" /><line
                x1="14"
                y1="18"
                x2="14"
                y2="11"
              />
              <line x1="18" y1="18" x2="18" y2="11" /><polygon
                points="12 2 20 7 4 7"
              />
            </svg>
          </div>
          <span class="text-sm font-semibold text-zinc-900">MyRentalTax</span>
        </div>
        <button
          class="w-7 h-7 flex items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 transition-colors"
          onclick={() => {
            sheetOpen = false;
          }}
          aria-label="Close navigation"
        >
          <XIcon class="w-4 h-4" />
        </button>
      </div>

      <!-- Scrollable body -->
      <div class="flex-1 overflow-y-auto p-2 flex flex-col gap-1">
        {@render navBody()}
      </div>

      <!-- Footer: user menu -->
      {@render userFooter()}
    </SheetContent>
  </Sheet>

  <!-- ── Main content ── -->
  <main
    class="flex-1 overflow-y-auto flex flex-col mt-[53px] md:mt-0"
    style="view-transition-name: main-content"
  >
    {@render children()}
  </main>
</div>

{#snippet sidebarContent()}
  <!-- Logo -->
  <div
    class="flex items-center gap-2 px-3 py-4 border-b border-zinc-200 flex-shrink-0"
  >
    <div
      class="w-8 h-8 bg-zinc-900 rounded-lg flex items-center justify-center text-white flex-shrink-0"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <line x1="3" y1="22" x2="21" y2="22" /><line
          x1="6"
          y1="18"
          x2="6"
          y2="11"
        />
        <line x1="10" y1="18" x2="10" y2="11" /><line
          x1="14"
          y1="18"
          x2="14"
          y2="11"
        />
        <line x1="18" y1="18" x2="18" y2="11" /><polygon
          points="12 2 20 7 4 7"
        />
      </svg>
    </div>
    <span class="text-sm font-semibold text-zinc-900">MyRentalTax</span>
  </div>

  <div class="flex-1 overflow-y-auto p-2 flex flex-col gap-1">
    {@render navBody()}
  </div>

  <!-- Footer: user menu -->
  {@render userFooter()}
{/snippet}

{#snippet navBody()}
  <!-- Property switcher -->
  <div class="relative mb-1">
    <button
      class="w-full flex items-center justify-between gap-2 px-2.5 py-2 border border-zinc-200 rounded-md bg-transparent cursor-pointer text-sm font-medium text-zinc-900 hover:bg-zinc-50 transition-colors"
      onclick={toggleSwitcher}
    >
      <span
        class="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap"
      >
        {currentPropertyLabel}
      </span>
      <ChevronUpDownIcon class="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
    </button>

    {#if switcherOpen}
      <div
        class="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-md z-50 p-1"
        onclick={(e) => e.stopPropagation()}
        onkeydown={(e) => e.stopPropagation()}
        role="menu"
        tabindex="-1"
      >
        {#each $propertiesStore as p (p.id)}
          <button
            class="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-zinc-900 hover:bg-zinc-50 text-left"
            onclick={() => navigateTo(`/properties/${p.id}/rooms`)}
          >
            <span class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
              >{p.name}</span
            >
            <span class="text-xs text-zinc-400 flex-shrink-0"
              >{p.city ?? ""}</span
            >
          </button>
        {/each}
        <div class="h-px bg-zinc-100 my-1"></div>
        <button
          class="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-zinc-500 hover:bg-zinc-50 text-left"
          onclick={() => navigateTo("/properties")}
        >
          <HomeIcon class="w-3.5 h-3.5" />
          All properties
        </button>
        <button
          class="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-zinc-500 hover:bg-zinc-50 text-left"
          onclick={() => navigateTo("/properties/new")}
        >
          <PlusIcon class="w-3.5 h-3.5" />
          Add new property
        </button>
      </div>
    {/if}
  </div>

  <!-- Consolidated Tax Report link -->
  {#if ($page.props.properties ?? []).length > 1}
    <a
      class="flex items-center gap-2 px-2.5 py-2 rounded-md text-sm text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors no-underline"
      href="/consolidated_report"
      onclick={(e) => {
        e.preventDefault();
        navigateTo("/consolidated_report");
      }}
    >
      <FileSpreadsheetIcon class="w-4 h-4 flex-shrink-0" />
      Consolidated Tax Report
    </a>
  {/if}

  <!-- Wizard steps nav -->
  <p
    class="text-[11px] font-semibold text-zinc-400 tracking-wider px-2 pt-2 pb-1"
  >
    WIZARD STEPS
  </p>
  <nav class="flex flex-col gap-px">
    {#each navItems as item}
      {@const isActive = item.pathMatch && $page.url.includes(item.pathMatch)}
      {@const isDisabled = item.path === null}
      <a
        class="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors no-underline {isActive
          ? 'bg-zinc-100 text-zinc-900 font-medium'
          : isDisabled
            ? 'text-zinc-300 pointer-events-none'
            : 'text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900'}"
        href={item.path ?? "#"}
        onclick={(e) => {
          e.preventDefault();
          if (item.path) navigateTo(item.path);
        }}
      >
        <svelte:component this={item.component} class="w-4 h-4 flex-shrink-0" />
        {item.label}
      </a>
    {/each}
  </nav>
{/snippet}

{#snippet userFooter()}
  {#if $page.props.current_user_email}
    <div class="border-t border-zinc-100 px-2 py-2 flex-shrink-0">
      <div class="flex items-center gap-2 w-full min-w-0 py-[7px]">
        <!-- Avatar initials -->
        <div
          class="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-semibold text-zinc-700 flex-shrink-0"
        >
          {emailInitials($page.props.current_user_email)}
        </div>

        <!-- Name + email (Popover trigger for name editing) -->
        <Popover.Root bind:open={namePopoverOpen}>
          <Popover.Trigger
            class="flex-1 min-w-0 text-left flex flex-col leading-tight gap-0.5"
          >
            {#if userName}
              <span class="truncate text-xs font-medium text-zinc-900"
                >{userName}</span
              >
            {:else}
              <span
                class="text-xs text-zinc-400 border-b border-dashed border-zinc-300 w-fit cursor-pointer hover:text-zinc-600 hover:border-zinc-400 transition-colors"
                >What is your name?</span
              >
            {/if}
            <span class="truncate text-xs text-zinc-400"
              >{$page.props.current_user_email}</span
            >
          </Popover.Trigger>
          <Popover.Content side="top" align="start" class="w-64 p-3">
            <div class="flex flex-col gap-3">
              <div class="flex flex-col gap-1.5">
                <label
                  class="text-xs font-medium text-zinc-700"
                  for="profile-name-input">Your name</label
                >
                <Input
                  id="profile-name-input"
                  bind:value={nameInput}
                  placeholder="Enter your name"
                  class="h-8 text-sm"
                  onkeydown={(e) => {
                    if (e.key === "Enter") saveName();
                  }}
                />
              </div>
              <button
                onclick={saveName}
                class="h-8 rounded-md bg-zinc-900 px-3 text-xs font-medium text-white hover:bg-zinc-800 transition-colors"
              >
                Save
              </button>
            </div>
          </Popover.Content>
        </Popover.Root>

        <!-- Dots button → Logout dropdown -->
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            class="w-6 h-6 flex items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 transition-colors flex-shrink-0"
            aria-label="User menu"
          >
            <EllipsisVerticalIcon class="w-4 h-4" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content side="top" align="end" class="w-40">
            <DropdownMenu.Item onclick={logout}>
              <LogOutIcon />
              Log out
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>
    </div>
  {/if}
{/snippet}
